/**
 * Catch length rules (#85), shared by the frontend (`@shared/catchLength`) and
 * the catch Lambdas (re-exported from `amplify/functions/_shared/species.ts`),
 * so the form and the server can never disagree.
 *
 * September 2026 accepted a Tailor entered at 500cm (meant 50cm). These rules
 * catch that kind of typo (an extra or swapped digit); they do not question
 * genuinely big fish. Legal size is a separate rule and is unchanged:
 * undersize fish may be submitted if released (`validateLegalSize`).
 */

/**
 * Hard cap for any species (D2). Above any fish club members catch in the
 * Tweed; anything over is a typo. Rejected in the forms and on the server.
 */
export const MAX_CATCH_LENGTH_CM = 200;

export const MAX_CATCH_LENGTH_MESSAGE = 'That length is too long to be right. Please check it.';

/**
 * Per-species "unusual" length in cm (D1). Over this, the form asks the angler
 * to confirm, and the server sets `lengthUnusual: true` so adjudication shows
 * "Check length". Each value sits well above a winning fish (e.g. September's
 * winning flathead was 92cm).
 *
 * OTHER covers any non-category species (e.g. cod, snapper, cobia), so it uses
 * the 150cm brag mat length: a fish longer than the mat is unusual whatever
 * the species.
 */
export const UNUSUAL_SIZES: Record<string, number> = {
  FLATHEAD: 120,
  BREAM: 60,
  JEWFISH: 170,
  MANGROVE_JACK: 90,
  WHITING: 55,
  TAILOR: 100,
  TREVALLY: 110,
  OTHER: 150,
};

/** True when a length is over the hard cap. */
export function isOverMaxLength(lengthCm: number): boolean {
  return lengthCm > MAX_CATCH_LENGTH_CM;
}

/** True when a length is over its species' unusual size (unknown species: never). */
export function isLengthUnusual(species: string | null | undefined, lengthCm: number | null | undefined): boolean {
  if (!species || typeof lengthCm !== 'number' || Number.isNaN(lengthCm)) return false;
  const limit = UNUSUAL_SIZES[species];
  return typeof limit === 'number' && lengthCm > limit;
}
